import LegalDocument from '@/components/legal/LegalDocument'

// Base terms of service. Review with counsel before relying on it.
export default function Tos({ last_updated }: { last_updated: string }) {
  return (
    <LegalDocument title="Terms of Service" lastUpdated={last_updated} contactEmail="legal@getnereus.ai">
      <p>
        These Terms of Service ("Terms") govern your access to and use of the Nereus freight
        operations platform and related services (the "Service"), operated by Nereus AI Inc
        ("Nereus", "we", "us"). By accessing or using the Service, you agree to these Terms. If you
        are using the Service on behalf of an organization, you represent that you are authorized to
        bind that organization.
      </p>

      <h2>1. The Service</h2>
      <p>
        Nereus helps freight forwarders and logistics teams manage operational email, quotes,
        bookings, and shipment documents, including by reading and drafting email on connected
        inboxes and exchanging data with carriers. Features may change over time.
      </p>

      <h2>2. Accounts and eligibility</h2>
      <p>
        You must provide accurate account information and keep your credentials secure. You are
        responsible for activity under your account. You must be able to form a binding contract to
        use the Service.
      </p>

      <h2>3. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service unlawfully or to send unlawful, infringing, or harmful content.</li>
        <li>Attempt to access accounts or data that are not yours, or probe or breach security.</li>
        <li>Interfere with or disrupt the integrity or performance of the Service.</li>
        <li>Reverse engineer or resell the Service except as permitted by law.</li>
      </ul>

      <h2>4. Connected mailboxes and third-party services</h2>
      <p>
        When you connect an email inbox or other third-party service (for example Google or a
        carrier API), you authorize Nereus to access and process data from that service to provide
        the Service. Your use of third-party services is also governed by their terms. You can
        disconnect a mailbox at any time from settings.
      </p>

      <h2>5. Customer data and ownership</h2>
      <p>
        As between you and Nereus, you retain all rights to the data you submit or connect ("Customer
        Data"). You grant us a limited license to host, process, and transmit Customer Data solely to
        provide and improve the Service. We own the Service and all related intellectual property.
      </p>

      <h2>6. Automated processing</h2>
      <p>
        The Service uses automation and AI, including third-party model providers, to read and
        generate text. AI output may be inaccurate or incomplete. You are responsible for reviewing
        AI-assisted drafts and decisions before relying on or sending them.
      </p>

      <h2>7. Fees</h2>
      <p>
        Fees, if any, will be set out in a separate order or agreement. Unless otherwise stated, fees
        are non-refundable and exclusive of taxes.
      </p>

      <h2>8. Confidentiality</h2>
      <p>
        Each party will protect the other's confidential information and use it only as needed to
        perform under these Terms.
      </p>

      <h2>9. Disclaimers</h2>
      <p>
        The Service is provided "as is" and "as available" without warranties of any kind, whether
        express, implied, or statutory, including merchantability, fitness for a particular purpose,
        and non-infringement. We do not warrant that the Service will be uninterrupted or error-free.
      </p>

      <h2>10. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, Nereus will not be liable for any indirect,
        incidental, special, consequential, or punitive damages, or for lost profits or revenues. Our
        total liability arising out of or relating to the Service will not exceed the amounts you paid
        us for the Service in the twelve months before the event giving rise to the claim.
      </p>

      <h2>11. Indemnification</h2>
      <p>
        You will defend and indemnify Nereus against claims arising from your Customer Data or your
        use of the Service in violation of these Terms or applicable law.
      </p>

      <h2>12. Termination</h2>
      <p>
        You may stop using the Service at any time. We may suspend or terminate access if you breach
        these Terms or to protect the Service. On termination, your right to use the Service ends and
        we may delete Customer Data after a reasonable period.
      </p>

      <h2>13. Governing law</h2>
      <p>
        These Terms are governed by the laws of the State of California, without regard to
        conflict-of-laws rules, and the state and federal courts located in California will have
        exclusive jurisdiction over disputes, except where prohibited by applicable law.
      </p>

      <h2>14. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will be reflected by updating
        the "Last updated" date above and, where appropriate, by additional notice. Continued use of
        the Service after changes take effect constitutes acceptance.
      </p>

      <h2>15. Contact</h2>
      <p>
        Questions about these Terms? Email{' '}
        <a href="mailto:legal@getnereus.ai">legal@getnereus.ai</a>.
      </p>
    </LegalDocument>
  )
}
